import { useCallback, useEffect, useRef, useState } from "react";
import * as Cesium from "cesium";
import "cesium/Build/Cesium/Widgets/widgets.css";
import api from "../../services/api";
import { useTheme } from "../ThemeProvider";
import AddressAutocompleteInput from "../AddressAutocompleteInput";

function ringToPositions(ring) {
  const flat = [];
  for (let i = 0; i < ring.length - 1; i++) {
    flat.push(ring[i][0], ring[i][1]);
  }
  return Cesium.Cartesian3.fromDegreesArray(flat);
}

function formatHour(h) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function defaultSummerDate() {
  const y = new Date().getFullYear();
  return `${y}-06-21`;
}

async function applyMapLayer(viewer, mode) {
  viewer.imageryLayers.removeAll();
  if (mode === "satellite") {
    const token = import.meta.env.VITE_CESIUM_ION_TOKEN || "";
    if (token) {
      try {
        const provider = await Cesium.IonImageryProvider.fromAssetId(2);
        viewer.imageryLayers.addImageryProvider(provider);
        return;
      } catch {
        /* fallback Esri */
      }
    }
    viewer.imageryLayers.addImageryProvider(
      new Cesium.UrlTemplateImageryProvider({
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        maximumLevel: 19,
        credit: new Cesium.Credit("Esri, Maxar, Earthstar Geographics"),
      })
    );
    return;
  }
  viewer.imageryLayers.addImageryProvider(
    new Cesium.UrlTemplateImageryProvider({
      url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
      maximumLevel: 19,
      credit: new Cesium.Credit("© OpenStreetMap"),
    })
  );
}

function parseNum(v) {
  if (v == null || v === "") return null;
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function fallbackTargetHeight(meta) {
  const altPlanta = parseNum(meta?.altura_planta) ?? 3.5;
  const edificio = parseNum(meta?.plantas_edificio);
  if (edificio) return edificio * altPlanta;
  const inmueble = parseNum(meta?.plantas_inmueble);
  if (inmueble) return inmueble * altPlanta;
  const m2 = parseNum(meta?.m2_parcela) ?? parseNum(meta?.m2_construidos) ?? 120;
  if (m2 < 65) return 4.5;
  if (m2 < 110) return 5.5;
  if (m2 < 210) return 7;
  if (m2 < 340) return 8.5;
  return 10;
}

function estimateFootprintRing(lat, lng, m2) {
  const side = Math.sqrt(m2);
  const half = side / 2;
  const R = 6378137;
  const dLat = (half / R) * (180 / Math.PI);
  const dLng = (half / (R * Math.cos((lat * Math.PI) / 180))) * (180 / Math.PI);
  return [
    [lng - dLng, lat - dLat],
    [lng + dLng, lat - dLat],
    [lng + dLng, lat + dLat],
    [lng - dLng, lat + dLat],
    [lng - dLng, lat - dLat],
  ];
}

function modalSize() {
  return {
    w: Math.round(window.innerWidth * 0.75),
    h: Math.round(window.innerHeight * 0.75),
  };
}

function modalOrigin(size) {
  return {
    x: Math.max(8, Math.round((window.innerWidth - size.w) / 2)),
    y: Math.max(24, Math.round((window.innerHeight - size.h) / 2)),
  };
}

function useDraggable(initialPos, size) {
  const [pos, setPos] = useState(initialPos);
  const drag = useRef(null);

  const onDragStart = useCallback(
    (e) => {
      if (e.button !== 0 || e.target.closest("button, input, select, textarea, a")) return;
      drag.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: pos.x,
        origY: pos.y,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [pos.x, pos.y]
  );

  const onDragMove = useCallback(
    (e) => {
      if (!drag.current) return;
      const dx = e.clientX - drag.current.startX;
      const dy = e.clientY - drag.current.startY;
      setPos({
        x: Math.max(8, Math.min(window.innerWidth - size.w - 8, drag.current.origX + dx)),
        y: Math.max(8, Math.min(window.innerHeight - size.h - 8, drag.current.origY + dy)),
      });
    },
    [size.w, size.h]
  );

  const onDragEnd = useCallback((e) => {
    drag.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  }, []);

  return { pos, onDragStart, onDragMove, onDragEnd };
}

export default function ModalVisorSol({
  open,
  onClose,
  lat,
  lng,
  referencia,
  initialAddress = "",
  propiedadMeta = {},
}) {
  const { theme } = useTheme();
  const t = theme.colors;
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const entitiesRef = useRef([]);

  const [size] = useState(modalSize);
  const { pos, onDragStart, onDragMove, onDragEnd } = useDraggable(modalOrigin(size), size);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [geoError, setGeoError] = useState("");
  const [buildingsCount, setBuildingsCount] = useState(0);
  const [targetFound, setTargetFound] = useState(false);
  const [fecha, setFecha] = useState(defaultSummerDate);
  const [horaDecimal, setHoraDecimal] = useState(12);
  const [vistaMapa, setVistaMapa] = useState("satellite");
  const [direccion, setDireccion] = useState(initialAddress);

  const baseLat = Number(lat);
  const baseLng = Number(lng);
  const [activeLat, setActiveLat] = useState(baseLat);
  const [activeLng, setActiveLng] = useState(baseLng);
  const hasCoords = Number.isFinite(activeLat) && Number.isFinite(activeLng);

  const destroyViewerRef = useRef(null);

  const destroyViewer = useCallback(() => {
    entitiesRef.current = [];
    if (viewerRef.current && !viewerRef.current.isDestroyed()) {
      viewerRef.current.destroy();
    }
    viewerRef.current = null;
  }, []);

  destroyViewerRef.current = destroyViewer;

  const handleClose = useCallback(() => {
    destroyViewerRef.current?.();
    onClose();
  }, [onClose]);

  const osmQueryParams = useCallback(
    () => ({
      lat: activeLat,
      lng: activeLng,
      radio: 120,
      plantas_edificio: propiedadMeta.plantas_edificio ?? "",
      plantas_inmueble: propiedadMeta.plantas_inmueble ?? "",
      altura_planta: propiedadMeta.altura_planta ?? "",
      m2_parcela: propiedadMeta.m2_parcela ?? "",
      m2_construidos: propiedadMeta.m2_construidos ?? "",
    }),
    [activeLat, activeLng, propiedadMeta]
  );

  useEffect(() => {
    if (!open) return undefined;
    setActiveLat(baseLat);
    setActiveLng(baseLng);
    setDireccion(initialAddress || "");
    setError("");
    setGeoError("");
    const onKey = (e) => {
      if (e.key === "Escape" && !document.querySelector("[data-places-suggest-root]")) {
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, baseLat, baseLng, initialAddress, handleClose]);

  const applySunTime = useCallback((viewer, dateStr, hourDec) => {
    if (!viewer || viewer.isDestroyed()) return;
    const hh = Math.floor(hourDec);
    const mm = Math.round((hourDec - hh) * 60);
    const d = new Date(`${dateStr}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`);
    if (Number.isNaN(d.getTime())) return;
    viewer.clock.currentTime = Cesium.JulianDate.fromDate(d);
    viewer.clock.shouldAnimate = false;
    if (viewer.scene.sun) viewer.scene.sun.show = true;
    if (viewer.scene.moon) viewer.scene.moon.show = false;
    viewer.scene.requestRender();
  }, []);

  const clearEntities = useCallback((viewer) => {
    for (const ent of entitiesRef.current) {
      viewer.entities.remove(ent);
    }
    entitiesRef.current = [];
  }, []);

  const addBuildingEntity = useCallback((viewer, ring, heightM, isTarget, name) => {
    const entity = viewer.entities.add({
      name,
      polygon: {
        hierarchy: ringToPositions(ring),
        height: 0,
        extrudedHeight: heightM,
        material: isTarget
          ? Cesium.Color.fromCssColorString("#f59e0b").withAlpha(0.88)
          : Cesium.Color.fromCssColorString("#64748b").withAlpha(0.78),
        outline: true,
        outlineColor: isTarget
          ? Cesium.Color.fromCssColorString("#b45309")
          : Cesium.Color.fromCssColorString("#334155").withAlpha(0.85),
        outlineWidth: isTarget ? 2 : 1,
        shadows: Cesium.ShadowMode.ENABLED,
        closeTop: true,
        closeBottom: false,
      },
    });
    entitiesRef.current.push(entity);
    return entity;
  }, []);

  const focusOnPoint = useCallback((viewer, la, ln, range = 220) => {
    if (!viewer || viewer.isDestroyed()) return;
    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(ln, la, range),
      orientation: {
        heading: Cesium.Math.toRadians(30),
        pitch: Cesium.Math.toRadians(-40),
        roll: 0,
      },
      duration: 0.8,
    });
  }, []);

  const focusOnEntities = useCallback(
    async (viewer, entities) => {
      if (!viewer || viewer.isDestroyed() || !entities.length) return;
      try {
        await viewer.flyTo(entities, {
          duration: 0.8,
          offset: new Cesium.HeadingPitchRange(
            Cesium.Math.toRadians(25),
            Cesium.Math.toRadians(-40),
            200
          ),
        });
      } catch {
        focusOnPoint(viewer, activeLat, activeLng);
      }
    },
    [activeLat, activeLng, focusOnPoint]
  );

  const renderScene = useCallback(
    async (viewer) => {
      clearEntities(viewer);

      const m2 =
        parseNum(propiedadMeta.m2_parcela) ??
        parseNum(propiedadMeta.m2_construidos) ??
        120;
      const estH = fallbackTargetHeight(propiedadMeta);
      const estRing = estimateFootprintRing(activeLat, activeLng, m2);
      const placeholder = addBuildingEntity(
        viewer,
        estRing,
        estH,
        true,
        referencia ? `Ref. ${referencia} (cargando…)` : "Vivienda (cargando…)"
      );
      applySunTime(viewer, fecha, horaDecimal);
      viewer.scene.requestRender();
      await focusOnEntities(viewer, [placeholder]);
      setLoading(false);

      let res;
      try {
        res = await api.get("/geo/edificios-osm", { params: osmQueryParams() });
      } catch (e) {
        setError(
          e?.response?.data?.error ||
            e?.message ||
            "No se pudieron cargar los edificios OSM"
        );
        return;
      }

      clearEntities(viewer);
      const buildings = res.data?.data?.buildings || [];
      setBuildingsCount(buildings.length);

      const targetEntities = [];
      let foundTarget = false;

      for (const b of buildings) {
        const h = b.height || 9;
        const ent = addBuildingEntity(
          viewer,
          b.ring,
          h,
          b.isTarget,
          b.isTarget ? (referencia ? `Ref. ${referencia}` : "Vuestra vivienda") : "Edificio colindante"
        );
        if (b.isTarget) {
          foundTarget = true;
          targetEntities.push(ent);
        }
      }

      setTargetFound(foundTarget);

      if (!foundTarget) {
        const ent = addBuildingEntity(
          viewer,
          estRing,
          estH,
          true,
          referencia ? `Ref. ${referencia} (estimado)` : "Vivienda (estimada)"
        );
        targetEntities.push(ent);
      }

      applySunTime(viewer, fecha, horaDecimal);
      viewer.scene.requestRender();

      if (targetEntities.length) {
        await focusOnEntities(viewer, targetEntities);
      } else {
        focusOnPoint(viewer, activeLat, activeLng);
      }
    },
    [
      activeLat,
      activeLng,
      addBuildingEntity,
      applySunTime,
      clearEntities,
      fecha,
      focusOnEntities,
      focusOnPoint,
      horaDecimal,
      osmQueryParams,
      propiedadMeta,
      referencia,
    ]
  );

  useEffect(() => {
    if (!open || !hasCoords || !containerRef.current) return undefined;

    let cancelled = false;
    setLoading(true);
    setError("");

    Cesium.Ion.defaultAccessToken = import.meta.env.VITE_CESIUM_ION_TOKEN || "";

    const viewer = new Cesium.Viewer(containerRef.current, {
      animation: false,
      timeline: false,
      baseLayerPicker: false,
      geocoder: false,
      homeButton: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      fullscreenButton: false,
      infoBox: false,
      selectionIndicator: false,
      shadows: true,
      terrainProvider: new Cesium.EllipsoidTerrainProvider(),
    });

    viewerRef.current = viewer;
    viewer.scene.globe.enableLighting = true;
    viewer.scene.globe.depthTestAgainstTerrain = false;
    viewer.scene.shadowMap.maximumDistance = 800;
    viewer.scene.shadowMap.size = 2048;
    viewer.scene.shadowMap.softShadows = true;
    viewer.scene.requestRenderMode = true;
    viewer.scene.maximumRenderTimeChange = Infinity;

    (async () => {
      try {
        await applyMapLayer(viewer, vistaMapa);
        await renderScene(viewer);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.error ||
              e?.message ||
              "No se pudieron cargar los edificios OSM"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      destroyViewer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeLat, activeLng, hasCoords]);

  useEffect(() => {
    if (!open) return;
    setDireccion(initialAddress || "");
  }, [open, initialAddress]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed() || !open) return;
    applySunTime(viewer, fecha, horaDecimal);
  }, [fecha, horaDecimal, open, applySunTime]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed() || !open) return;
    let alive = true;
    (async () => {
      try {
        await applyMapLayer(viewer, vistaMapa);
        if (alive) viewer.scene.requestRender();
      } catch {
        /* capa por defecto */
      }
    })();
    return () => {
      alive = false;
    };
  }, [vistaMapa, open]);

  useEffect(() => {
    if (!open || !containerRef.current) return undefined;
    const ro = new ResizeObserver(() => {
      const v = viewerRef.current;
      if (v && !v.isDestroyed()) v.resize();
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [open]);

  const irADireccion = useCallback(async (addressText, latitud, longitud) => {
    setGeoError("");
    if (latitud != null && longitud != null) {
      setActiveLat(Number(latitud));
      setActiveLng(Number(longitud));
      return;
    }
    const q = String(addressText || direccion).trim();
    if (q.length < 5) {
      setGeoError("Escribe o elige una dirección");
      return;
    }
    try {
      const res = await api.post("/geo/geocode-direccion", { address: q });
      if (!res.data?.ok) {
        setGeoError("No se encontró la dirección");
        return;
      }
      setActiveLat(Number(res.data.latitud));
      setActiveLng(Number(res.data.longitud));
    } catch (e) {
      setGeoError(e?.response?.data?.error || "Error al geocodificar");
    }
  }, [direccion]);

  const onPlaceResolved = useCallback(
    async (parts) => {
      const line =
        [parts.calle, parts.numero, parts.poblacion].filter(Boolean).join(", ") ||
        parts.calle ||
        direccion;
      setDireccion(line);
      await irADireccion(line, parts.latitud, parts.longitud);
    },
    [direccion, irADireccion]
  );

  if (!open) return null;

  const labelSt = {
    display: "block",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: t.textMuted,
    marginBottom: 4,
    marginTop: 8,
  };

  const inputSt = {
    width: "100%",
    padding: "4px 6px",
    borderRadius: 6,
    border: `1px solid ${t.border}`,
    background: t.bgInput,
    color: t.textPrimary,
    fontSize: 11,
    boxSizing: "border-box",
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, pointerEvents: "none" }}>
      <div
        style={{
          pointerEvents: "auto",
          position: "absolute",
          left: pos.x,
          top: pos.y,
          width: size.w,
          height: size.h,
          display: "flex",
          flexDirection: "column",
          borderRadius: 12,
          overflow: "hidden",
          border: `1px solid ${t.border}`,
          boxShadow: "0 16px 48px rgba(0,0,0,0.35)",
          background: theme.name === "dark" ? "#0f172a" : "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            background: theme.name === "dark" ? "#1e293b" : "#f8fafc",
            borderBottom: `1px solid ${t.border}`,
            flexShrink: 0,
          }}
        >
          <div
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            style={{ flex: 1, cursor: "grab", userSelect: "none", paddingRight: 8 }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, color: t.textPrimary }}>
              ☀️ Visor Sol {referencia ? `· Ref. ${referencia}` : ""}
            </div>
            <div style={{ fontSize: 10, color: t.textMuted, marginTop: 2 }}>
              75 % pantalla · arrastra aquí · Esc para cerrar
            </div>
          </div>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            style={{
              border: `1px solid ${t.border}`,
              borderRadius: 6,
              background: theme.name === "dark" ? "#334155" : "#e2e8f0",
              fontSize: 16,
              width: 28,
              height: 28,
              cursor: "pointer",
              color: t.textPrimary,
              lineHeight: 1,
              flexShrink: 0,
            }}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, position: "relative", minHeight: 0 }}>
          {!hasCoords && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                textAlign: "center",
                color: t.textMuted,
                fontSize: 12,
              }}
            >
              Indica coordenadas en Localización o busca otra dirección abajo.
            </div>
          )}

          {hasCoords && (
            <>
              <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

              <div
                style={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  zIndex: 5,
                  width: 260,
                  maxHeight: "calc(100% - 16px)",
                  overflowY: "auto",
                  padding: 10,
                  borderRadius: 10,
                  background:
                    theme.name === "dark"
                      ? "rgba(15,23,42,0.92)"
                      : "rgba(255,255,255,0.96)",
                  border: `1px solid ${t.border}`,
                  fontSize: 11,
                  color: t.textPrimary,
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <label style={{ ...labelSt, marginTop: 0 }}>Dirección alternativa</label>
                <AddressAutocompleteInput
                  value={direccion}
                  onValueChange={setDireccion}
                  onPlaceAddressResolved={onPlaceResolved}
                  placeholder="Calle, número, población…"
                  placesLocalityContext={
                    hasCoords
                      ? { latitude: activeLat, longitude: activeLng }
                      : null
                  }
                  style={inputSt}
                  onInputKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      irADireccion(direccion, null, null);
                    }
                  }}
                />
                {geoError && (
                  <p style={{ margin: "4px 0 0", color: "#ef4444", fontSize: 10 }}>{geoError}</p>
                )}

                {loading && (
                  <p style={{ margin: "8px 0 0", color: t.textMuted }}>
                    Cargando mapa 3D…
                  </p>
                )}
                {error && (
                  <p style={{ margin: "8px 0 0", color: "#ef4444", lineHeight: 1.4 }}>{error}</p>
                )}
                {!loading && !error && (
                  <>
                    <p style={{ margin: "8px 0 0", color: t.textMuted }}>
                      {buildingsCount} edificios en 120 m
                    </p>
                    <p style={{ margin: "0 0 4px", color: targetFound ? "#16a34a" : "#d97706" }}>
                      {targetFound
                        ? "Vivienda identificada en OSM (naranja)"
                        : "Sin huella OSM: volumen estimado"}
                    </p>
                  </>
                )}

                <label style={labelSt}>Vista del mapa</label>
                <div style={{ display: "flex", gap: 6 }}>
                  {[
                    { id: "satellite", label: "🛰️ Satélite" },
                    { id: "calle", label: "🗺️ Calle" },
                  ].map(({ id, label }) => {
                    const activo = vistaMapa === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setVistaMapa(id)}
                        style={{
                          flex: 1,
                          padding: "5px 6px",
                          borderRadius: 6,
                          border: `1px solid ${activo ? "#f59e0b" : t.border}`,
                          background: activo
                            ? theme.name === "dark"
                              ? "#422006"
                              : "#fffbeb"
                            : t.bgInput,
                          color: activo ? (theme.name === "dark" ? "#fde68a" : "#92400e") : t.textPrimary,
                          fontSize: 10,
                          fontWeight: activo ? 700 : 500,
                          cursor: "pointer",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>

                <label style={labelSt}>Fecha</label>
                <input
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  style={inputSt}
                />

                <label style={labelSt}>Hora — {formatHour(horaDecimal)}</label>
                <input
                  type="range"
                  min={5}
                  max={22}
                  step={0.25}
                  value={horaDecimal}
                  onChange={(e) => setHoraDecimal(parseFloat(e.target.value))}
                  style={{ width: "100%", accentColor: "#f59e0b" }}
                />

                <p style={{ margin: "8px 0 0", fontSize: 9, color: t.textMuted, lineHeight: 1.45 }}>
                  Altura automática según OSM. Naranja = vuestra vivienda; gris = colindantes.
                  La sombra sigue la hora del sol (hora local España).
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
